import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { createProvider } from '@frontend/store';
import { contextFactory } from '@frontend/design-system';
import type { CalendarEntityType } from '../../stores/calendar.store';
import { OverlappingEventIndicatorVariantType } from './calendar-view-v3/overlapping-event-indicator';
import { PROVIDER_COLUMN_MIN_WIDTH } from './constants';
import { CalendarViewOffices, EventType } from './types';
import { getProviderColumnLaneCount } from './utils';

type FloatingTooltipData = {
  name: string;
  value: string;
  appointmentType?: string;
};

type CalendarViewStore = {
  backgroundView: boolean;
  setBackgroundView: (backgroundView: boolean) => void;
  hoveredTimeProgressPosition: number;
  setHoveredTimeProgressPosition: (hoveredTimeProgressPosition: number) => void;
  containerState: {
    isLeft: boolean;
    isRight: boolean;
    isScrollable: boolean;
    clientWidth: number;
  };
  floatingTooltipData: undefined | FloatingTooltipData;
  setFloatingTooltipData: (floatingTooltipData: undefined | FloatingTooltipData) => void;
  setContainerState: (containerState: {
    isLeft: boolean;
    isRight: boolean;
    isScrollable: boolean;
    clientWidth: number;
  }) => void;

  visibleLaneCount: number;
  providerColumnWidth: number;
  setVisibleLaneCountBasedOnTotalWidth: (providerColumnWidth: number) => void;

  // Scroll container ref for calendar auto-scroll functionality
  scrollContainerRef: HTMLElement | null;
  setScrollContainerRef: (ref: HTMLElement | null) => void;
};

export const { Provider: CalendarViewProvider, useStore: useCalendarView } = createProvider<CalendarViewStore>()(
  (set) => ({
    backgroundView: false,
    hoveredTimeProgressPosition: 0,
    containerState: {
      isLeft: false,
      isRight: false,
      isScrollable: false,
      clientWidth: 0,
    },
    floatingTooltipData: undefined,
    setFloatingTooltipData: (floatingTooltipData) => {
      set(() => ({ floatingTooltipData }));
    },
    setContainerState: (containerState) => {
      set(() => ({ containerState }));
    },
    setHoveredTimeProgressPosition: (hoveredTimeProgressPosition) => {
      set(() => ({ hoveredTimeProgressPosition }));
    },
    setBackgroundView: (backgroundView) => {
      set(() => ({ backgroundView }));
    },
    providerColumnWidth: PROVIDER_COLUMN_MIN_WIDTH,
    visibleLaneCount: 2,
    setVisibleLaneCountBasedOnTotalWidth: (providerColumnWidth) => {
      const visibleLaneCount = getProviderColumnLaneCount(providerColumnWidth);
      set(() => ({ visibleLaneCount, providerColumnWidth }));
    },
    scrollContainerRef: null,
    setScrollContainerRef: (ref) => {
      set(() => ({ scrollContainerRef: ref }));
    },
  })
);

export type CalendarViewProps = {
  /**
   * An optional number that represents the starting hour of the calendar view.
   * If not provided, the default value is 0 (Which represents 12AM).
   */
  startHour?: number;
  /**
   * An optional number that represents the ending hour of the calendar view.
   * If not provided, the default value is 24 (Which represents 12AM next day).
   */
  endHour?: number;
  /**
   *  An optional function that is called when a new event is created.
   *  It takes in three parameters: ***startHour*** which is a string representing the
   *  starting hour of the event, ***endHour*** which is a string representing the
   *  ending hour of the event, and ***providerName*** which is a string representing
   *  the name of the provider associated with the event.
   */
  onEventCreate?: (
    startHour: string,
    endHour: string,
    providerName: string | undefined,
    providerId: string | undefined,
    isValid: boolean,
    locationId: string,
    calendarDateValue: string
  ) => void;
  /**
   * A required property that represents the data for the calendar view.
   */
  data: CalendarViewOffices;

  /**
   * An optional string that represents the id for that specific calendar view
   */
  id?: string;

  /**
   * A required property that represents Calendar event Card Component
   */
  EventCardComponent: React.FC<EventCardComponentProps>;

  EmptyCalendarComponent?: React.FC;

  /**
   * A boolean that represents if the event creation should be allowed or not.
   */
  shouldAllowEventCreation?: boolean;
  calendarDate: string;
};

export const [CalendarViewProps, useCalendarViewProps] = contextFactory<CalendarViewProps>();

export type EventCardComponentProps = {
  id: string;
  title: string;
  eventType: EventType;
  locationName?: string;
  duration?: string;
  providerId?: string;
  providerName?: string;
  locationId?: string;
  isExpanded: boolean;
  startDate: string;
  // v3 specific props
  overlappingIndicatorVariant?: OverlappingEventIndicatorVariantType;
  isYesterdayEvent?: boolean;
  scheduleRequest?: ScheduleRequest;
};

export type ReorderConfirmHandlerResponse = [CalendarEntityType, string, Record<string, number>];
type ReorderHandler = { confirm: () => ReorderConfirmHandlerResponse; cancel: () => void };
// Context and hook for managing calendar column entities' drag and drop state
type ColumnEntityReorderStore = {
  isInReorderMode: boolean;
  setIsInReorderMode: (isInReorderMode: boolean) => void;
  // string key is the droppableId to which the dragged column belongs to
  columnReorderHandlers: Record<string, ReorderHandler> | undefined;
  addToColumnReorderHandlers: (locationId: string, handler: ReorderHandler) => void;
  removeColumnReorderHandler: (locationId: string) => void;
  isReordering: boolean;
  setIsReordering: (isReordering: boolean) => void;
  resetColumnReorderHandlers: () => void;
};

export const { Provider: ColumnEntityReorderStoreProvider, useStore: useColumnEntityReorderStore } =
  createProvider<ColumnEntityReorderStore>()((set) => ({
    // indicates if we are in reorder mode
    isInReorderMode: false,
    setIsInReorderMode: (isInReorderMode) => {
      set({ isInReorderMode });
    },
    // indicates if the confirm action is being processed
    isReordering: false,
    setIsReordering: (isReordering) => {
      set({ isReordering });
    },
    columnReorderHandlers: undefined,
    addToColumnReorderHandlers: (locationId, handler) => {
      set((state) => {
        const updatedHandlers = state.columnReorderHandlers ? { ...state.columnReorderHandlers } : {};
        updatedHandlers[locationId] = handler;
        return { columnReorderHandlers: updatedHandlers, isInReorderMode: true };
      });
    },
    removeColumnReorderHandler: (locationId: string) => {
      set((state) => {
        if (!state.columnReorderHandlers) {
          return {};
        }
        const updatedHandlers = { ...state.columnReorderHandlers };
        delete updatedHandlers[locationId];
        if (updatedHandlers && Object.keys(updatedHandlers).length === 0) {
          return { columnReorderHandlers: undefined, isReordering: false };
        }
        return { columnReorderHandlers: updatedHandlers };
      });
    },
    resetColumnReorderHandlers: () => {
      set({ columnReorderHandlers: undefined, isInReorderMode: false, isReordering: false });
    },
  }));
